/**
 * Consumers Tab
 * 
 * Manage API consumers (3rd parties who access our APIs via Kong)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
  People as PeopleIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';

import apiManagerApi from './api';

interface Consumer {
  consumer_id: string;
  consumer_name: string;
  description: string;
  allowed_routes: string[];
  auth_type: string;
  rate_limits: {
    minute?: number;
    hour?: number;
    day?: number;
  };
  status: string;
  created_at: string;
}

export default function ConsumersTab() {
  const [consumers, setConsumers] = useState<Consumer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    consumer_name: '',
    description: '',
    allowed_routes: '/api/telemetry',
    contact_email: '',
    rate_limits_minute: 100,
    rate_limits_hour: 1000
  });

  useEffect(() => {
    loadConsumers();
  }, []);

  const loadConsumers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiManagerApi.listConsumers();
      setConsumers(response.data.consumers || []);
    } catch (err: any) {
      console.error('Failed to load consumers:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load consumers');
      setConsumers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConsumer = async () => {
    try {
      setCreating(true);
      setError(null);

      const response = await apiManagerApi.createConsumer({
        consumer_name: formData.consumer_name,
        description: formData.description,
        allowed_routes: formData.allowed_routes.split(',').map(r => r.trim()),
        auth_type: 'api_key',
        contact_email: formData.contact_email || undefined,
        rate_limits: {
          minute: formData.rate_limits_minute,
          hour: formData.rate_limits_hour
        }
      });

      // Show API key
      setNewApiKey(response.data.api_key);
      
      // Reload list
      loadConsumers();
    } catch (err: any) {
      console.error('Failed to create consumer:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create consumer');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteConsumer = async (consumerId: string) => {
    if (!confirm('Are you sure you want to delete this consumer? Their API key will stop working.')) return;

    try {
      await apiManagerApi.deleteConsumer(consumerId);
      loadConsumers();
    } catch (err: any) {
      console.error('Failed to delete consumer:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete consumer');
    }
  };

  const handleRegenerateKey = async (consumerId: string) => {
    if (!confirm('Are you sure? The old API key will stop working immediately.')) return;

    try {
      const response = await apiManagerApi.regenerateApiKey(consumerId);
      setNewApiKey(response.data.api_key);
    } catch (err: any) {
      console.error('Failed to regenerate API key:', err);
      setError(err.response?.data?.error || err.message || 'Failed to regenerate key');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a snackbar notification here
  };

  const resetForm = () => {
    setFormData({
      consumer_name: '',
      description: '',
      allowed_routes: '/api/telemetry',
      contact_email: '',
      rate_limits_minute: 100,
      rate_limits_hour: 1000
    });
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewApiKey(null);
    resetForm();
  };

  const getStatusColor = (status: string): any => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'warning';
      case 'revoked':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ px: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          API Consumers
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh list">
            <IconButton onClick={loadConsumers} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Consumer
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && consumers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <PeopleIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No API Consumers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create API consumers to allow 3rd parties to access your APIs via Kong Gateway
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Consumer
          </Button>
        </Box>
      )}

      {/* Consumer Cards */}
      {!loading && consumers.length > 0 && (
        <Grid container spacing={3}>
          {consumers.map((consumer) => (
            <Grid item xs={12} md={6} key={consumer.consumer_id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {consumer.consumer_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {consumer.description}
                      </Typography>
                    </Box>
                    <Chip
                      label={consumer.status.toUpperCase()}
                      size="small"
                      color={getStatusColor(consumer.status)}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Allowed Routes:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {consumer.allowed_routes.map((route, idx) => (
                        <Chip key={idx} label={route} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Rate Limits:
                    </Typography>
                    <Typography variant="body2">
                      {consumer.rate_limits.minute}/min, {consumer.rate_limits.hour}/hr
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<VpnKeyIcon />}
                      onClick={() => handleRegenerateKey(consumer.consumer_id)}
                    >
                      Regenerate Key
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteConsumer(consumer.consumer_id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Consumer Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && closeCreateDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {newApiKey ? 'Consumer Created' : 'Create API Consumer'}
        </DialogTitle>
        <DialogContent>
          {newApiKey ? (
            <Box sx={{ pt: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Consumer created successfully! Save this API key - it won't be shown again.
              </Alert>
              <Paper sx={{ p: 2, bgcolor: 'grey.100', wordBreak: 'break-all', mb: 2 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {newApiKey}
                </Typography>
              </Paper>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={() => copyToClipboard(newApiKey)}
              >
                Copy to Clipboard
              </Button>
            </Box>
          ) : (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Consumer Name"
                value={formData.consumer_name}
                onChange={(e) => setFormData({ ...formData, consumer_name: e.target.value })}
                fullWidth
                required
                placeholder="acme_corp"
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                label="Allowed Routes (comma-separated)"
                value={formData.allowed_routes}
                onChange={(e) => setFormData({ ...formData, allowed_routes: e.target.value })}
                fullWidth
                required
                placeholder="/api/telemetry, /api/devices"
              />
              <TextField
                label="Contact Email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                fullWidth
                type="email"
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Rate Limit (per minute)"
                    value={formData.rate_limits_minute}
                    onChange={(e) => setFormData({ ...formData, rate_limits_minute: parseInt(e.target.value) || 0 })}
                    fullWidth
                    type="number"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Rate Limit (per hour)"
                    value={formData.rate_limits_hour}
                    onChange={(e) => setFormData({ ...formData, rate_limits_hour: parseInt(e.target.value) || 0 })}
                    fullWidth
                    type="number"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={creating}>
            {newApiKey ? 'Close' : 'Cancel'}
          </Button>
          {!newApiKey && (
            <Button
              onClick={handleCreateConsumer}
              variant="contained"
              disabled={creating || !formData.consumer_name}
            >
              {creating ? <CircularProgress size={24} /> : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

